import React, { useState } from 'react';
import {
  IconButton,
  Tooltip,
  Popover,
  Box,
  Typography,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Download as DownloadIcon,
  TableChart as TableIcon,
  Description as CsvIcon,
  Email as EmailIcon,
  PictureAsPdf as PictureAsPdfIcon,
} from '@mui/icons-material';
import {
  exportToCSV,
  exportToExcel,
  coerceValueForExcelCell,
  ExportFormat,
  ExportColumn,
  ExportOptions,
} from '../utils/exportUtils';

interface ExportButtonProps {
  data: any[];
  columns: ExportColumn[];
  filename?: string;
  disabled?: boolean;
  loading?: boolean;
  dataGridRef?: React.RefObject<any>;
  exportAllData?: boolean; // If true, exports all data; if false, exports current page
  onExportStart?: () => void;
  onExportComplete?: () => void;
  onExportError?: (error: Error) => void;
  columnVisibilityModel?: Record<string, boolean>;
  sx?: any;
  hasEmailColumn?: boolean;
  onEmailExport?: () => Promise<void>;
  hasPdfExport?: boolean;
  onPdfExport?: () => Promise<void>;
  isPdfDownloading?: boolean;
  /** When true, hide Excel and CSV options (e.g. for PDF-only reports like Chapter Star). */
  disableTableExport?: boolean;
  /** When true with table export enabled, show only Excel (.xlsx), not CSV or page/export-all switch. */
  excelOnly?: boolean;
}

const ExportButton: React.FC<ExportButtonProps> = ({
  data,
  columns,
  filename = 'export',
  disabled = false,
  loading = false,
  dataGridRef,
  exportAllData = true,
  onExportStart,
  onExportComplete,
  onExportError,
  columnVisibilityModel,
  sx = {},
  hasEmailColumn = false,
  onEmailExport,
  hasPdfExport = false,
  onPdfExport,
  isPdfDownloading = false,
  disableTableExport = false,
  excelOnly = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportAll, setExportAll] = useState(exportAllData);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEmailExport = async () => {
    if (!onEmailExport) return;

    try {
      setIsExporting(true);
      onExportStart?.();
      await onEmailExport();
      onExportComplete?.();
    } catch (error) {
      console.error('Email export failed:', error);
      onExportError?.(error as Error);
    } finally {
      setIsExporting(false);
      handleClose();
    }
  };

  const handleExport = async (format: ExportFormat) => {
    if (!data || data.length === 0) {
      console.warn('No data to export');
      return;
    }

    try {
      setIsExporting(true);
      onExportStart?.();

      // Force DataGrid to render all columns by temporarily disabling column virtualization
      if (dataGridRef?.current) {
        try {
          const apiRef = dataGridRef.current;

          // Try to disable column virtualization temporarily
          if (apiRef.setColumnVirtualizationEnabled) {
            console.log('Disabling column virtualization for export...');
            apiRef.setColumnVirtualizationEnabled(false);

            // Wait for the grid to re-render with all columns
            await new Promise((resolve) => setTimeout(resolve, 200));

            // Try to get all columns after disabling virtualization
            if (apiRef.getAllColumns) {
              const allCols = apiRef.getAllColumns();
              console.log(
                'All columns after disabling virtualization:',
                allCols
              );
            }

            // Re-enable column virtualization after export
            setTimeout(() => {
              try {
                apiRef.setColumnVirtualizationEnabled(true);
                console.log('Re-enabled column virtualization');
              } catch (e) {
                console.log('Failed to re-enable column virtualization:', e);
              }
            }, 1000);
          } else {
            console.log(
              'setColumnVirtualizationEnabled not available, trying scroll method...'
            );

            // Fallback: Try to scroll to the rightmost column to force rendering
            if (apiRef.scrollToIndexes) {
              const lastColumnIndex = columns.length - 1;
              apiRef.scrollToIndexes({ colIndex: lastColumnIndex });
              await new Promise((resolve) => setTimeout(resolve, 100));
            }
          }
        } catch (e) {
          console.log('Failed to force column rendering:', e);
        }
      }

      // Prepare data for export
      let exportData = data;

      // Get visible columns from DataGrid API
      let exportColumns = [...columns]; // Start with original columns

      if (dataGridRef?.current) {
        try {
          const apiRef = dataGridRef.current;

          // Use the passed columnVisibilityModel prop first, then try DataGrid state
          let visibilityModel = columnVisibilityModel || {};

          // Method 1: Check state.columnVisibility
          if (apiRef.state?.columnVisibility) {
            visibilityModel = {
              ...visibilityModel,
              ...apiRef.state.columnVisibility,
            };
          }

          // Method 2: Check state.columnVisibilityModel
          if (apiRef.state?.columnVisibilityModel) {
            visibilityModel = {
              ...visibilityModel,
              ...apiRef.state.columnVisibilityModel,
            };
          }

          // Method 3: Try to get visible columns using the DataGrid API
          if (apiRef.getVisibleColumns) {
            try {
              const visibleCols = apiRef.getVisibleColumns();

              if (visibleCols && visibleCols.length > 0) {
                // Map visible columns to our export format
                exportColumns = visibleCols
                  .filter((col: any) => col.field && col.visible !== false)
                  .map((col: any) => ({
                    field: col.field,
                    headerName: col.headerName || col.field,
                    width: col.width || col.computedWidth || 150,
                  }));
              }
            } catch (e) {
              // getVisibleColumns() failed, continue with fallback
            }
          }

          // If we didn't get columns from getVisibleColumns, try visibility model
          if (
            exportColumns.length === columns.length &&
            Object.keys(visibilityModel).length > 0
          ) {
            exportColumns = columns.filter((col) => {
              const isHidden = visibilityModel[col.field] === false;
              return !isHidden;
            });
          }

          // Try to get additional column info from DataGrid API (for width, etc.)
          if (apiRef.getAllColumns) {
            const allCols = apiRef.getAllColumns();

            if (allCols && allCols.length > 0) {
              // Create a map of DataGrid column info for reference
              const dataGridColumnMap = new Map();
              allCols.forEach((col: any) => {
                if (col.field) {
                  dataGridColumnMap.set(col.field, {
                    width: col.width || col.computedWidth || 150,
                    headerName: col.headerName || col.field,
                    hide: col.hide,
                  });
                }
              });

              // Update our export columns with DataGrid info (width, headerName)
              exportColumns = exportColumns.map((col) => {
                const dataGridInfo = dataGridColumnMap.get(col.field);
                if (dataGridInfo) {
                  return {
                    ...col,
                    width: dataGridInfo.width,
                    headerName:
                      dataGridInfo.headerName || col.headerName || col.field,
                  };
                }
                return col;
              });
            }
          }
        } catch (e) {
          // Failed to get column visibility info, continue with original columns
        }
      }

      // Apply filters if available
      if (dataGridRef?.current) {
        const apiRef = dataGridRef.current;

        if (apiRef.state?.filter?.filterModel) {
          const filterModel = apiRef.state.filter.filterModel;

          if (Object.keys(filterModel).length > 0) {
            exportData = exportData.filter((row) => {
              return Object.entries(filterModel).every(
                ([field, filter]: [string, any]) => {
                  if (!filter) return true;
                  const value = row[field];
                  const filterValue = (filter as any).value;

                  if ((filter as any).operator === 'contains') {
                    return String(value)
                      .toLowerCase()
                      .includes(String(filterValue).toLowerCase());
                  }
                  if ((filter as any).operator === 'equals') {
                    return value === filterValue;
                  }
                  if ((filter as any).operator === 'startsWith') {
                    return String(value)
                      .toLowerCase()
                      .startsWith(String(filterValue).toLowerCase());
                  }
                  if ((filter as any).operator === 'endsWith') {
                    return String(value)
                      .toLowerCase()
                      .endsWith(String(filterValue).toLowerCase());
                  }
                  return true;
                }
              );
            });
          }
        }

        // Apply sorting if available
        if (apiRef.state?.sorting?.sortModel) {
          const sortModel = apiRef.state.sorting.sortModel;

          if (sortModel.length > 0) {
            exportData.sort((a, b) => {
              for (const sort of sortModel) {
                const aValue = a[sort.field];
                const bValue = b[sort.field];

                let comparison = 0;
                if (aValue < bValue) comparison = -1;
                if (aValue > bValue) comparison = 1;

                if (comparison !== 0) {
                  return sort.sort === 'desc' ? -comparison : comparison;
                }
              }
              return 0;
            });
          }
        }

        // Handle pagination if not exporting all data
        if (!exportAll && apiRef.state?.pagination?.paginationModel) {
          const { page, pageSize } = apiRef.state.pagination.paginationModel;
          const startIndex = page * pageSize;
          const endIndex = startIndex + pageSize;
          exportData = exportData.slice(startIndex, endIndex);
          console.log(
            `Exporting page ${page + 1}: rows ${startIndex}-${endIndex}`
          );
        }
      }

      // Ensure we have columns even if all methods failed
      if (exportColumns.length === 0) {
        console.warn(
          'No columns found from any method, using fallback columns'
        );
        exportColumns = columns;
      } else {
        console.log(
          `Successfully detected ${exportColumns.length} columns for export`
        );
      }

      console.log('Final export data:', {
        dataLength: exportData.length,
        columnsLength: exportColumns.length,
        columns: exportColumns.map((c) => ({ field: c.field, width: c.width })),
      });

      // Format data: CSV keeps stringification; Excel preserves numbers for SheetJS
      const formattedData =
        format === 'excel'
          ? exportData.map((row) => {
              const formattedRow: Record<string, string | number> = {};
              exportColumns.forEach((col) => {
                formattedRow[col.field] = coerceValueForExcelCell(
                  row[col.field]
                );
              });
              return formattedRow;
            })
          : exportData.map((row) => {
              const formattedRow: Record<string, string> = {};
              exportColumns.forEach((col) => {
                const value = row[col.field];

                if (value === null || value === undefined) {
                  formattedRow[col.field] = '';
                } else if (value instanceof Date) {
                  formattedRow[col.field] = value.toLocaleDateString();
                } else if (typeof value === 'boolean') {
                  formattedRow[col.field] = value ? 'Yes' : 'No';
                } else if (typeof value === 'object') {
                  formattedRow[col.field] = JSON.stringify(value);
                } else {
                  formattedRow[col.field] = String(value);
                }
              });
              return formattedRow;
            });

      // Export based on format
      const options: ExportOptions = {
        filename,
        includeHeaders: true,
      };

      if (format === 'csv') {
        exportToCSV(formattedData, exportColumns, options);
      } else if (format === 'excel') {
        exportToExcel(formattedData, exportColumns, options);
      }

      onExportComplete?.();
    } catch (error) {
      console.error('Export failed:', error);
      onExportError?.(error as Error);
    } finally {
      setIsExporting(false);
      handleClose();
    }
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <Tooltip title="Export Data">
        <IconButton
          onClick={handleClick}
          disabled={disabled || loading || (data.length === 0 && !hasPdfExport)}
          sx={{
            backgroundColor: 'white',
            color: 'primary.main',
            border: '1px solid',
            borderColor: 'primary.main',
            '&:hover': {
              backgroundColor: 'grey.50',
              borderColor: 'primary.dark',
            },
            ...sx,
          }}
        >
          {isExporting ? <CircularProgress size={20} /> : <DownloadIcon />}
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            width: 280,
            maxHeight: 400,
            overflow: 'auto',
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
            Export Data
          </Typography>

          {!disableTableExport && !excelOnly && (
            <>
              {/* Export Options */}
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={exportAll}
                      onChange={(e) => setExportAll(e.target.checked)}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2">Export all data</Typography>
                  }
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 0.5 }}
                >
                  {exportAll
                    ? 'Exports all filtered and sorted data'
                    : 'Exports current page only'}
                </Typography>
              </Box>

              <Divider sx={{ my: 1 }} />
            </>
          )}

          {/* Export Format Options */}
          <List dense>
            {!disableTableExport && (
              <>
                <ListItem
                  button
                  onClick={() => handleExport('excel')}
                  disabled={isExporting}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                >
                  <ListItemIcon>
                    <TableIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Excel (.xlsx)"
                    secondary="Best for data analysis and sharing"
                  />
                </ListItem>

                {!excelOnly && (
                  <ListItem
                    button
                    onClick={() => handleExport('csv')}
                    disabled={isExporting}
                    sx={{
                      borderRadius: 1,
                      mb: hasEmailColumn ? 0.5 : 0,
                      '&:hover': { backgroundColor: 'action.hover' },
                    }}
                  >
                    <ListItemIcon>
                      <CsvIcon color="secondary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="CSV (.csv)"
                      secondary="Universal format, works everywhere"
                    />
                  </ListItem>
                )}
              </>
            )}

            {hasEmailColumn && onEmailExport && (
              <ListItem
                button
                onClick={handleEmailExport}
                disabled={isExporting}
                sx={{
                  borderRadius: 1,
                  mb: hasPdfExport && onPdfExport ? 0.5 : 0,
                  '&:hover': { backgroundColor: 'action.hover' },
                }}
              >
                <ListItemIcon>
                  <EmailIcon color="info" />
                </ListItemIcon>
                <ListItemText
                  primary="Email List"
                  secondary="Export email addresses only"
                />
              </ListItem>
            )}

            {hasPdfExport && onPdfExport && (
              <ListItem
                button
                onClick={async () => {
                  try {
                    setIsExporting(true);
                    await onPdfExport();
                    handleClose();
                  } catch {
                    // Error handled by parent (e.g. snackbar)
                  } finally {
                    setIsExporting(false);
                  }
                }}
                disabled={isExporting || isPdfDownloading}
                sx={{
                  borderRadius: 1,
                  '&:hover': { backgroundColor: 'action.hover' },
                }}
              >
                <ListItemIcon>
                  <PictureAsPdfIcon color="error" />
                </ListItemIcon>
                <ListItemText
                  primary="PDF"
                  secondary="Full report for current filters"
                />
              </ListItem>
            )}
          </List>

          {!disableTableExport && (
            <Box
              sx={{ mt: 2, p: 1, backgroundColor: 'grey.50', borderRadius: 1 }}
            >
              {excelOnly ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block' }}
                >
                  Ready to export {data.length} record
                  {data.length !== 1 ? 's' : ''} • {columns.length} columns
                </Typography>
              ) : (
                <>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block' }}
                  >
                    {exportAll
                      ? `Ready to export ${data.length} records`
                      : `Current page: ${data.length} records`}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 0.5 }}
                  >
                    {columns.length} columns • Includes filters & sorting
                  </Typography>
                </>
              )}
            </Box>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default ExportButton;
